import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { IFinancialOperation } from "dto/Banking/IBanking";
import { generateFinancialOperationMetricPdf } from "utils/generateFinancialOperationMetricPdf";
import PDFViewer from "components/PDFViewer";
import styles from "./styles.module.scss";

interface Props {
  operation: IFinancialOperation;
  onClose: () => void;
}

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const fmtAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat("pl-PL", { style: "currency", currency: currency || "PLN" }).format(amount);

const FinancialOperationMetricSlide = ({ operation, onClose }: Props) => {
  const [generating, setGenerating] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  const isCredit = operation.Direction === "Credit";

  const handleShowPdf = async () => {
    setGenerating(true);
    try {
      const blob = await generateFinancialOperationMetricPdf({
        id: operation.Id,
        aspspName: operation.AspspName,
        accountName: operation.AccountName,
        accountIban: operation.AccountIban,
        bookingDate: operation.BookingDate,
        valueDate: operation.ValueDate,
        direction: operation.Direction,
        amount: operation.Amount,
        currency: operation.Currency,
        counterpartyName: operation.CounterpartyName,
        counterpartyIban: operation.CounterpartyIban,
        title: operation.Title,
        status: operation.Status,
        balanceAfter: operation.BalanceAfter,
        bankTransactionCode: operation.BankTransactionCode,
        reference: operation.Reference,
      });
      setPdfBlob(blob);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {pdfBlob && (
        <div className={styles.pdfPreview}>
          <PDFViewer
            file={pdfBlob}
            title="Metryka operacji finansowej"
            showCloseButton
            onCloseButton={() => setPdfBlob(null)}
            showSignButton
          />
        </div>
      )}

      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Metryka operacji</span>
            {operation.BookingDate && (
              <span className={styles.headerSub}>{fmtDate(operation.BookingDate)}</span>
            )}
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.downloadBtn}
              onClick={handleShowPdf}
              disabled={generating}
            >
              {generating ? "Ładowanie…" : "PDF"}
            </button>
            <button className={styles.closeBtn} onClick={onClose} title="Zamknij">
              ✕
            </button>
          </div>
        </div>

        <div className={styles.body}>
          {/* Dane operacji */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("info")}>
            <span>Dane operacji</span>
            <FontAwesomeIcon
              className={styles.sectionChevron}
              icon={collapsed.info ? faChevronRight : faChevronDown}
            />
          </div>
          {!collapsed.info && (
            <div className={styles.metaCard}>
              {operation.Title && (
                <div className={styles.metaTitleRow}>
                  <div className={styles.metaTitleLabel}>Tytuł</div>
                  <div className={styles.metaTitleValue}>{operation.Title}</div>
                </div>
              )}
              <div className={styles.amountRow}>
                <span className={isCredit ? styles.amountCredit : styles.amountDebit}>
                  {isCredit ? "+" : "−"}{fmtAmount(operation.Amount, operation.Currency)}
                </span>
                <span className={styles.directionBadge} data-direction={operation.Direction}>
                  {isCredit ? "Wpływ" : "Wydatek"}
                </span>
              </div>
              <div className={styles.metaGrid}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Data księgowania</span>
                  <span className={styles.metaValue}>{fmtDate(operation.BookingDate)}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Data waluty</span>
                  <span className={styles.metaValue}>{fmtDate(operation.ValueDate)}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Status</span>
                  <span className={styles.metaValue}>
                    {operation.Status === "Pending" ? "Oczekująca" : "Zaksięgowana"}
                  </span>
                </div>
                {operation.BalanceAfter != null && (
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Saldo po operacji</span>
                    <span className={styles.metaValue}>
                      {fmtAmount(operation.BalanceAfter, operation.Currency)}
                    </span>
                  </div>
                )}
                {operation.Reference && (
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Referencja</span>
                    <span className={styles.metaValue}>{operation.Reference}</span>
                  </div>
                )}
                {operation.BankTransactionCode && (
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Kod transakcji</span>
                    <span className={styles.metaValue}>{operation.BankTransactionCode}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Kontrahent */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("counterparty")}>
            <span>Kontrahent</span>
            <FontAwesomeIcon
              className={styles.sectionChevron}
              icon={collapsed.counterparty ? faChevronRight : faChevronDown}
            />
          </div>
          {!collapsed.counterparty && (
            <div className={styles.metaCard}>
              {operation.CounterpartyName || operation.CounterpartyIban ? (
                <div className={styles.metaGrid}>
                  {operation.CounterpartyName && (
                    <div className={`${styles.metaItem} ${styles.metaItemFull}`}>
                      <span className={styles.metaLabel}>Nazwa</span>
                      <span className={styles.metaValue}>{operation.CounterpartyName}</span>
                    </div>
                  )}
                  {operation.CounterpartyIban && (
                    <div className={`${styles.metaItem} ${styles.metaItemFull}`}>
                      <span className={styles.metaLabel}>Numer rachunku</span>
                      <span className={styles.metaValue}>{operation.CounterpartyIban}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.noData}>Brak danych kontrahenta</div>
              )}
            </div>
          )}

          {/* Rachunek bankowy */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("account")}>
            <span>Rachunek bankowy</span>
            <FontAwesomeIcon
              className={styles.sectionChevron}
              icon={collapsed.account ? faChevronRight : faChevronDown}
            />
          </div>
          {!collapsed.account && (
            <div className={styles.metaCard}>
              <div className={styles.metaGrid}>
                {operation.AspspName && (
                  <div className={`${styles.metaItem} ${styles.metaItemFull}`}>
                    <span className={styles.metaLabel}>Bank</span>
                    <span className={styles.metaValue}>{operation.AspspName}</span>
                  </div>
                )}
                {(operation.AccountName || operation.AccountIban) && (
                  <div className={`${styles.metaItem} ${styles.metaItemFull}`}>
                    <span className={styles.metaLabel}>Rachunek</span>
                    <span className={styles.metaValue}>
                      {operation.AccountName || operation.AccountIban}
                    </span>
                  </div>
                )}
                {operation.AccountName && operation.AccountIban && (
                  <div className={`${styles.metaItem} ${styles.metaItemFull}`}>
                    <span className={styles.metaLabel}>IBAN</span>
                    <span className={styles.metaValue}>{operation.AccountIban}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialOperationMetricSlide;
